import React, { useState } from "react";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { tp } from "../i18n";
import { Button, Input, Select } from "./ui";
import { Loader2, Save } from "lucide-react";

const empty = { full_name: "", organization: "", address_line1: "", address_line2: "", city: "", region: "", postal_code: "", country: "", phone1: "", phone2: "", email1: "", email2: "", memorable_word: "", memorable_phrase: "", preferred_contact: "email", timezone: "" };

export const ProfileForm = ({ initial, initialConsents, onSaved, showConsents = true }) => {
  const { lang } = useApp();
  const [f, setF] = useState({ ...empty, ...(initial || {}) });
  const [c, setC] = useState({ newsletter: false, tech_updates: false, grace_optin: false, ...(initialConsents || {}) });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = f.full_name.trim() && f.city.trim() && f.country.trim()
    && f.phone1.trim() && f.phone2.trim() && f.email1.trim() && f.email2.trim()
    && f.memorable_word.trim() && f.memorable_phrase.trim();

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await client.post("/account/profile", f);
      if (showConsents) await client.post("/account/consents", { newsletter: !!c.newsletter, tech_updates: !!c.tech_updates, grace_optin: !!c.grace_optin });
      onSaved && onSaved();
    } catch (e) { setErr(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="profile-form">
      <div className="grid gap-2 sm:grid-cols-2">
        <Input label={tp("Full name *", lang)} value={f.full_name} onChange={set("full_name")} data-testid="pf-name" />
        <Input label={tp("Organization", lang)} value={f.organization} onChange={set("organization")} data-testid="pf-org" />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input label={tp("1st phone *", lang)} value={f.phone1} onChange={set("phone1")} data-testid="pf-phone1" />
        <Input label={tp("2nd phone *", lang)} value={f.phone2} onChange={set("phone2")} data-testid="pf-phone2" />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input label={tp("1st email *", lang)} type="email" value={f.email1} onChange={set("email1")} data-testid="pf-email1" />
        <Input label={tp("2nd / backup email *", lang)} type="email" value={f.email2} onChange={set("email2")} data-testid="pf-email2" />
      </div>
      <Input label={tp("Address line 1", lang)} value={f.address_line1} onChange={set("address_line1")} data-testid="pf-addr1" />
      <Input label={tp("Address line 2", lang)} value={f.address_line2} onChange={set("address_line2")} data-testid="pf-addr2" />
      <div className="grid gap-2 sm:grid-cols-2">
        <Input label={tp("City *", lang)} value={f.city} onChange={set("city")} data-testid="pf-city" />
        <Input label={tp("State / Region", lang)} value={f.region} onChange={set("region")} data-testid="pf-region" />
        <Input label={tp("Postal code", lang)} value={f.postal_code} onChange={set("postal_code")} data-testid="pf-postal" />
        <Input label={tp("Country *", lang)} value={f.country} onChange={set("country")} data-testid="pf-country" />
      </div>
      <div className="rounded-md border border-black/10 dark:border-white/10 p-2.5 space-y-2">
        <p className="text-xs opacity-60">{tp("Confidential recovery hints \u2014 used by customer service to verify it's really you. Stored encrypted.", lang)}</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <Input label={tp("Memorable word *", lang)} value={f.memorable_word} onChange={set("memorable_word")} data-testid="pf-memword" />
          <Input label={tp("Memorable phrase or public body *", lang)} value={f.memorable_phrase} onChange={set("memorable_phrase")} data-testid="pf-memphrase" />
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Select label={tp("Preferred contact", lang)} value={f.preferred_contact} onChange={set("preferred_contact")} data-testid="pf-contact">
          <option value="email">{tp("Email", lang)}</option>
          <option value="phone">{tp("Phone", lang)}</option>
        </Select>
        <Input label={tp("Timezone (e.g. America/Toronto)", lang)} value={f.timezone} onChange={set("timezone")} data-testid="pf-tz" />
      </div>
      {showConsents && (
        <div className="space-y-1.5 rounded-md border border-black/10 dark:border-white/10 p-2.5">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!c.newsletter} onChange={(e) => setC({ ...c, newsletter: e.target.checked })} data-testid="pf-newsletter" /> {tp("Email me the FIDUX\u2122 Newsletter", lang)}</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!c.tech_updates} onChange={(e) => setC({ ...c, tech_updates: e.target.checked })} data-testid="pf-tech" /> {tp("Email me product & technical updates", lang)}</label>
          <label className="flex items-start gap-2 border-t border-black/10 dark:border-white/10 pt-2 text-sm"><input type="checkbox" className="mt-0.5" checked={!!c.grace_optin} onChange={(e) => setC({ ...c, grace_optin: e.target.checked })} data-testid="pf-grace" /> <span>{tp("Yes, I want the 30 day grace period to pay the Annual Service Fee.", lang)}<span className="block text-xs opacity-60">{tp("New customers only. A 15-day email + SMS reminder is sent (to the contact details you provide), and service is suspended on day 31 if the first fee has not been paid.", lang)}</span></span></label>
        </div>
      )}
      {err && <div className="text-sm text-[#DA1E28]" data-testid="pf-error">{err}</div>}
      <div className="flex items-center justify-end gap-2">
        {!valid && <span className="text-xs opacity-60">{tp("* required fields", lang)}</span>}
        <Button onClick={save} disabled={busy || !valid} data-testid="pf-save">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {tp("Save", lang)}
        </Button>
      </div>
    </div>
  );
};

export default ProfileForm;
